import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { Html } from "@react-three/drei";
import useHotspots from "../../hooks/useHotspots";
import useAnalytics from "../../hooks/useAnalytics";

const getSize = (sizeStr) => {
	switch (sizeStr) {
		case "small":
			return 16;
		case "large":
			return 26;
		default:
			return 20;
	}
};

const HotspotCircle = styled.div`
    background-color: ${({ $background }) => $background || "var(--d8s-bgHotspot)"};
    ${({ $color }) => $color && `color: ${$color};`}
    min-height: ${({ $size }) => $size}px;
    border-radius: 50%;
    aspect-ratio: 1 / 1;
    font-size: calc(${({ $size }) => $size}px * 0.75);
    opacity: ${({ $behind }) => $behind ? 0.5 : 1};
    display: ${({ $show }) => $show ? "flex" : "none"};
    cursor: ${({ $hasContent }) => $hasContent ? "pointer" : "default"};
    justify-content: center;
    align-items: center;
    transition: opacity 0.3s;
    user-select: none;

    &:hover {
        opacity: 1;
        box-shadow: 0 0 5px 0 var(--d8s-shadow);
    }
`;

const ExpandedHotspot = styled.div`
    background-color: color(from ${({ $background }) => $background || "var(--d8s-bgHotspot)"} srgb r g b / 0.9);
    ${({ $color }) => $color && `color: ${$color};`}

    border-radius: 8px;
    padding: 5px 10px;
    display: flex;
    flex-direction: column;
    min-width: 200px;
    opacity: ${({ $behind }) => $behind ? 0.5 : 1};
    box-shadow: 0 0 2px 0 var(--d8s-shadow);
    transition: opacity 0.3s;

    &:hover {
        opacity: 1;
    }

    h2 {
        font-size: 14px;
        margin: 2px 0;
    }

    p {
        font-size: 12px;
    }
`;

const Hotspot = ({
	                 id,
	                 meshId,
	                 meshName,
	                 label,
	                 title,
	                 text,
	                 position,
	                 size,
	                 expandOnClick,
	                 showOnHover,
	                 showOnLoad,
	                 modelEventsManager,
	                 background,
	                 color,
                 }) => {
	const [isOverMesh, setOverMesh] = useState(false);
	const [isExpanded, setExpanded] = useState(false);
	const [isBehind, setBehind] = useState(false);
	const isVisible = showOnLoad || (showOnHover && isOverMesh);
	const hasContent = !!(text || title);

	const onMouseEnter = () => {
		if (!expandOnClick && !isExpanded && hasContent) {
			setExpanded(true);
		}
	};

	const onMouseLeave = () => {
		if (!expandOnClick) {
			setExpanded(false);
		}
	};

	const onClick = () => {
		if (expandOnClick && hasContent) {
			setExpanded((prev) => !prev);
		}
	};

	useEffect(() => {
		const handlers = [];

		if (!showOnLoad && showOnHover) {
			handlers.push(modelEventsManager.addPointerOverListener((e) => {
				if (e?.object?.name === meshName) {
					setOverMesh(true);
				}
			}));

			handlers.push(modelEventsManager.addPointerOutListener((e) => {
				setOverMesh(false);
			}));
		}

		return () => {
			handlers.forEach((off) => off());
		};
	}, [showOnLoad, showOnHover]);

	const onOccluded = (isOccluded) => {
		setBehind(isOccluded);
	};

	return (<Html
		prepend
		occlude
		position={position}
		zIndexRange={[1000, 0]}
		onOcclude={onOccluded}
		dataForMesh={meshId}
		dataHotSpotId={id}
	>
		{!isExpanded && <HotspotCircle
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onClick={onClick}
			$size={getSize(size)}
			$behind={isBehind}
			$show={isVisible}
			$background={background}
			$color={color}
			$hasContent={hasContent}
		>
			{label}
		</HotspotCircle>}
		{isExpanded && hasContent &&
			<ExpandedHotspot
				onMouseLeave={onMouseLeave}
				onClick={onClick}
				$behind={isBehind}
				$background={background}
				$color={color}
			>
				{title && <h2>{title}</h2>}
				<p>{text}</p>
			</ExpandedHotspot>}
	</Html>);
};

const ModelHotspots = ({ getIsReady, renderingApi, modelEventsManager }) => {
	const { triggerAnalyticsEvent } = useAnalytics();
	const hotspots = useHotspots(getIsReady, renderingApi, triggerAnalyticsEvent);

	return hotspots?.map((data) => (
		<Hotspot key={data.id} {...data} modelEventsManager={modelEventsManager}/>
	));
};

export default ModelHotspots;
